import type { LC, PropsWithChildren, LiveElement } from '@use-gpu/live';
import type { LightEnv, PassFlags, PassResources, RenderComponents } from '../pass/types';

import { use, yeet, memo, useMemo, useOne } from '@use-gpu/live';

import { PassReconciler } from '../reconcilers/index';

import { DebugPass } from '../pass/debug-pass';
import { DeferredGPass } from '../pass/deferred-g-pass';
import { DeferredResolvePass } from '../pass/deferred-resolve-pass';
import { MotionPass } from '../pass/motion-pass';
import { OITPass } from '../pass/oit-pass';
import { OutlinePass } from '../pass/outline-pass';
import { PickingPass } from '../pass/picking-pass';
import { ShadowPass } from '../pass/shadow-pass';
import { SSAOPass } from '../pass/ssao-pass';

import { DebugRender } from './forward/debug';
import { PickingRender } from './forward/picking';
import { ShadedRender } from './forward/shaded';
import { ShadowRender } from './forward/shadow';
import { SolidRender } from './forward/solid';
import { UIRender } from './forward/ui';

import { useStandardBindGroups } from '../pass/bindings';
import { useMakeUseVariants } from '../pass/variants';

import { DeferredShadedRender } from './deferred/deferred-shaded';
import { DeferredSolidRender } from './deferred/deferred-solid';
import { DeferredUIRender } from './deferred/deferred-ui';

import { OITShadedRender } from './oit/oit-shaded';
import { OITSolidRender } from './oit/oit-solid';

import { LightProvider } from '../providers/light-provider';

import { Renderer } from './renderer';
import { LightRender } from './light/light-render';

const {quote} = PassReconciler;

const NO_RESOURCES: PassResources = {
  targets: {},
  bindings: {},
  dispatches: [],
  views: {},
};

const NO_OPTIONS: DeferredRendererOptions = {};

export type DeferredRendererOptions = Pick<PassFlags, 'shadows' | 'color' | 'merge' | 'overlay'>;

export type DeferredRendererProps = PropsWithChildren<{
  resources: PassResources,
  options?: DeferredRendererOptions,
  passes?: LiveElement[],
  subpasses?: LiveElement[],
  components?: RenderComponents,
}>;

const getComponents = ({modes = {}, renders = {}}: Partial<RenderComponents>): RenderComponents => {
  return {
    modes: {
      debug: DebugRender,
      picking: PickingRender,
      shadow: ShadowRender,
      ...modes,
    },
    renders: {
      solid: {opaque: DeferredSolidRender, transparent: SolidRender, oit: OITSolidRender},
      shaded: {opaque: DeferredShadedRender, transparent: ShadedRender, oit: OITShadedRender},
      ui: {opaque: DeferredUIRender, transparent: UIRender},
      ...renders,
    }
  }
};

/** Deferred-mode rendering with a G-Buffer. Lights are painted in afterwards using stencil volumes. */
export const DeferredRenderer: LC<DeferredRendererProps> = memo((props: DeferredRendererProps) => {
  const {
    resources = NO_RESOURCES,
    options = NO_OPTIONS,
    passes,
    subpasses,

    children,
  } = props;

  const {targets} = resources;

  const {
    color = true,
    overlay = false,
    merge = false,
    //overscan = 0,
    debug = null,

    facets = false,
    lights = true,
    motion = !!targets.motion,
    normals = !!targets.normal,
    oit = !!targets.oit,
    outline = targets.outline ? {} : undefined,
    picking = !!targets.picking,
    shadows = !!targets.shadow,
    ssao = targets.ssao ? {} : undefined,
  } = options as Record<string, any>;

  const extendedFlags = useMemo(() => ({
    color,
    overlay,
    merge,
    //overscan,

    facets,
    lights,
    motion,
    normals,
    oit,
    outline,
    picking,
    shadows,
    ssao,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [options, targets]);

  const components = useOne(() => getComponents(props.components ?? {}), props.components);

  // Prepare passes
  const resolved = useOne(() => passes ?? [
    motion ? use(MotionPass, options) : null,
    shadows ? use(ShadowPass, options) : null,
    use(DeferredGPass, options),
    ssao ? use(SSAOPass, options) : null,
    color ? use(DeferredResolvePass, options) : null,
    oit ? use(OITPass, options) : null,
    outline ? use(OutlinePass, options) : null,
    picking ? use(PickingPass, options) : null,
    debug ? use(DebugPass, options) : null,
    subpasses,
  ], props);

  // Add resource dispatches to render
  const dispatches = resources.dispatches?.length ? yeet({ dispatch: resources.dispatches }) : null;
  const combined = dispatches ? [
    quote(dispatches),
    children,
  ] : children;

  // Gather lights via capture provider
  // Emit deferred light draw calls
  const view = use(LightProvider, {
    shadows,
    children: combined,
    then: (light: LightEnv) =>
      useMemo(() => quote([
        yeet({ env: { light }}),
        use(LightRender, {...light, shadows}),
        // eslint-disable-next-line react-hooks/exhaustive-deps
      ]), [light, shadows]),
  });

  // Pass bindings
  const bindGroups = useStandardBindGroups(resources, extendedFlags);

  // Render variants
  const variants = useMakeUseVariants(components, extendedFlags);

  return (
    Renderer({
      resources,
      bindGroups,
      options,

      variants,
      passes: resolved,

      children: view,
    })
  );
}, 'DeferredRenderer');
